import { Link } from "react-router-dom";
import { useEffect } from "react";

import PublicSiteHeader from "../components/PublicSiteHeader";
import AppFooter from "../components/AppFooter";
import logo from "../assets/logo_basic.png";

export default function FoundationTruth() {
  // Lock document scroll only while this page is active.
  // The .app-body area remains the scrolling container,
  // consistent with the rest of the application layout.
  useEffect(() => {
    document.body.classList.add("app-lock");

    return () => {
      document.body.classList.remove("app-lock");
    };
  }, []);

  return (
    <div className="app-shell bg-[#020617] text-slate-200 antialiased">
      {/* =====================================================
          SHARED PUBLIC HEADER
      ===================================================== */}
      <PublicSiteHeader active="foundation" />

      {/* =====================================================
          PAGE BODY
      ===================================================== */}
      <main className="app-body overflow-x-hidden">
        <div className="px-6 py-10 md:py-14">
          <article className="max-w-3xl mx-auto">
            {/* =====================================================
                PAGE TITLE
            ===================================================== */}
            <header className="mb-10">
              <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.22em] text-sm mb-3">
                Our Story
              </p>

              <h1 className="text-3xl md:text-5xl font-extrabold text-white">
                Our Founding{" "}
                <span className="text-[#EB6AB5]">
                  Truth
                </span>
              </h1>

              <div className="h-1 w-16 bg-[#EB6AB5] rounded mt-4" />
            </header>

            {/* =====================================================
                INTRO
            ===================================================== */}
            <section className="space-y-5 text-base md:text-lg text-slate-300 leading-relaxed">
              <p>
                We built this platform because we understand what it feels like
                to be uncertain about the future of work. AI is changing
                industries faster than most people can adjust, and many are left
                wondering where they fit in anymore. We’ve felt that same
                uncertainty ourselves.
              </p>

              <div className="pl-5 border-l-2 border-[#EB6AB5] text-slate-400 italic text-sm md:text-base space-y-1">
                <p>
                  For a long time, we tried following the usual advice—lists of
                  trending jobs, generic online suggestions, and courses that
                  everyone seemed to recommend. But many of those paths led
                  nowhere. Some didn’t match who we were, others weren’t
                  meaningful, and many had no real opportunities behind them.
                  Over time,{" "}
                  <strong className="text-white not-italic">
                    realization crept in: the problem wasn’t effort, but the
                    lack of guidance that truly understood us as individuals.
                  </strong>
                </p>
              </div>

              <p>
                That realization is why this website exists.
              </p>
            </section>

            {/* =====================================================
                OUR PURPOSE
            ===================================================== */}
            <section className="pt-10 space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Our Purpose
              </h2>

              <p className="text-slate-300 leading-relaxed">
                Our goal is simple:{" "}
                <strong className="text-white">
                  to help you find a career direction that makes sense for
                  you—your strengths, your experience, your aspirations, and the
                  city you live in or hope to move to.
                </strong>
              </p>

              <p className="text-sm md:text-base text-slate-400">
                Not guesswork. Not blanket advice. Not whatever job title
                happens to be trending this year.
              </p>

              <p className="text-slate-300 leading-relaxed">
                Your personalized report brings together your background,
                skills, personality, and local job market insights to offer
                recommendations that are{" "}
                <strong className="text-[#EB6AB5]">
                  practical and relevant
                </strong>
                . It highlights risks, explores AI-resilient paths, identifies
                skill gaps, and points you toward training and employers in your
                city.
              </p>
            </section>

            {/* =====================================================
                WHAT COMES NEXT
            ===================================================== */}
            <section className="pt-10 space-y-5">
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                What Comes Next
              </h2>

              <p className="text-slate-300 leading-relaxed">
                This isn’t a one-time interaction. We’re building much more—
                supportive communities, local job links, discounted training
                options, and partnerships with employers who value people with
                real skills and clear direction. And as we grow, you’ll be among
                the first to know and benefit from everything new we add.
              </p>

              <p className="text-white font-medium pt-6 border-t border-slate-800 mt-8 leading-relaxed">
                We hope to walk this journey with you, not just point the way.
                <br />

                <span className="text-slate-400 font-normal">
                  And wherever your path leads, know that we’re quietly cheering
                  you on.
                </span>
              </p>
            </section>

            {/* =====================================================
                RETURN / CTA
            ===================================================== */}
            <div className="pt-12 pb-14 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/landing"
                className="
                  inline-flex
                  items-center
                  justify-center
                  px-8
                  py-3
                  text-sm
                  font-semibold
                  text-white
                  border
                  border-white/20
                  rounded-full
                  hover:bg-white
                  hover:text-[#11111f]
                  transition-all
                  duration-300
                "
              >
                ← Return to Homepage
              </Link>

              <Link
                to="/landing#pricing"
                className="
                  inline-flex
                  items-center
                  justify-center
                  px-8
                  py-3
                  text-sm
                  font-bold
                  text-white
                  bg-[#cb01bd]
                  rounded-full
                  hover:bg-[#e94ce0]
                  transition-all
                  duration-300
                  shadow-[0_0_20px_rgba(203,1,189,0.35)]
                  hover:shadow-[0_0_30px_rgba(203,1,189,0.55)]
                "
              >
                Get My Audit
              </Link>
            </div>
          </article>
        </div>

        {/* =====================================================
            SAME FOOTER AS FAQ / OTHER PUBLIC PAGES
        ===================================================== */}
        <AppFooter
          variant="landing"
          logoSrc={logo}
        />
      </main>
    </div>
  );
}